import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { Step5Data, HoursData } from '../../../types/onboarding';

interface Step5Props {
  data: Step5Data | null;
  onChange: (data: Step5Data) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
}

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const DEFAULT_HOURS: HoursData = {
  open: '09:00',
  close: '17:00',
  closed: false,
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export function Step5Location({
  data,
  onChange,
  onNext,
  onBack,
  saving,
}: Step5Props) {
  const [hasPhysicalLocation, setHasPhysicalLocation] = useState(data?.has_physical_location ?? true);
  const [streetAddress, setStreetAddress] = useState(data?.street_address || '');
  const [city, setCity] = useState(data?.city || '');
  const [state, setState] = useState(data?.state || '');
  const [zipCode, setZipCode] = useState(data?.zip_code || '');
  const [hours, setHours] = useState<Step5Data['hours']>(
    data?.hours || {
      monday: { ...DEFAULT_HOURS },
      tuesday: { ...DEFAULT_HOURS },
      wednesday: { ...DEFAULT_HOURS },
      thursday: { ...DEFAULT_HOURS },
      friday: { ...DEFAULT_HOURS },
      saturday: { ...DEFAULT_HOURS, open: '10:00', close: '18:00' },
      sunday: { ...DEFAULT_HOURS, closed: true },
    }
  );

  // Save current state to parent
  const saveData = (updatedHours?: Step5Data['hours'], updatedHasPhysicalLocation?: boolean) => {
    const hasPhysLoc = updatedHasPhysicalLocation ?? hasPhysicalLocation;
    onChange({
      has_physical_location: hasPhysLoc,
      street_address: hasPhysLoc ? streetAddress : undefined,
      city,
      state,
      zip_code: hasPhysLoc ? zipCode : undefined,
      hours: updatedHours ?? hours,
    });
  };

  // Handle toggle for physical location
  const handleTogglePhysicalLocation = (checked: boolean) => {
    const hasPhysLoc = !checked; // Checkbox is "I don't have a physical location"
    setHasPhysicalLocation(hasPhysLoc);
    saveData(undefined, hasPhysLoc);
  };

  const updateDayHours = (
    day: keyof Step5Data['hours'],
    field: keyof HoursData,
    value: string | boolean
  ) => {
    const newHours = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value,
      },
    };
    setHours(newHours);
    // Save immediately for hours changes since they're discrete selections
    saveData(newHours);
  };

  const applyWeekdayHours = () => {
    const mondayHours = hours.monday;
    const newHours = {
      ...hours,
      tuesday: { ...mondayHours },
      wednesday: { ...mondayHours },
      thursday: { ...mondayHours },
      friday: { ...mondayHours },
    };
    setHours(newHours);
    saveData(newHours);
  };

  // Validation: city & state always required; street/zip only if has physical location
  const canProceed = hasPhysicalLocation
    ? streetAddress.trim() && city.trim() && state && zipCode.trim()
    : city.trim() && state;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Location & Hours
        </h1>
        <p className="text-gray-600">
          Where can customers find you?
        </p>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Business Location</h2>

        {/* No physical location checkbox */}
        <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={!hasPhysicalLocation}
            onChange={(e) => handleTogglePhysicalLocation(e.target.checked)}
            className="w-5 h-5 mt-0.5 text-primary-600 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">
              I don't have a physical location customers can visit
            </span>
            <p className="text-sm text-gray-500 mt-0.5">
              For mobile services, online businesses, or home-based businesses
            </p>
          </div>
        </label>

        <div className="space-y-4">
          {/* Street Address - only shown if has physical location */}
          {hasPhysicalLocation && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address *
              </label>
              <input
                type="text"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                onBlur={() => saveData()}
                placeholder="123 Main Street"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* City, State, ZIP - layout changes based on physical location */}
          <div className={hasPhysicalLocation ? "grid grid-cols-3 gap-4" : "grid grid-cols-2 gap-4"}>
            <div className={hasPhysicalLocation ? "col-span-1" : ""}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onBlur={() => saveData()}
                placeholder="Chicago"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <select
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  // Save immediately for select since it's a discrete choice
                  onChange({
                    has_physical_location: hasPhysicalLocation,
                    street_address: hasPhysicalLocation ? streetAddress : undefined,
                    city,
                    state: e.target.value,
                    zip_code: hasPhysicalLocation ? zipCode : undefined,
                    hours,
                  });
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            {/* ZIP - only shown if has physical location */}
            {hasPhysicalLocation && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP *
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  onBlur={() => saveData()}
                  placeholder="60601"
                  maxLength={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
          </div>

          {/* Helper text when no physical location */}
          {!hasPhysicalLocation && (
            <p className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
              Your city and state will be used to show your service area on your website. The map will display a city-level view.
            </p>
          )}
        </div>
      </div>

      {/* Hours */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Business Hours</h2>
          <button
            onClick={applyWeekdayHours}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Apply Monday hours to weekdays
          </button>
        </div>

        <div className="space-y-3">
          {DAYS.map((day) => (
            <div
              key={day}
              className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
            >
              <div className="w-24 font-medium text-gray-700">
                {DAY_LABELS[day]}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hours[day].closed}
                  onChange={(e) => updateDayHours(day, 'closed', e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-600">Closed</span>
              </label>

              {!hours[day].closed && (
                <>
                  <input
                    type="time"
                    value={hours[day].open}
                    onChange={(e) => updateDayHours(day, 'open', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="time"
                    value={hours[day].close}
                    onChange={(e) => updateDayHours(day, 'close', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
